// @ts-nocheck
import React, {useEffect, useState} from 'react';
import {CardStyleInterpolators, createStackNavigator} from '@react-navigation/stack';
import SignInScreen from '../modules/Auth/screens/SignInScreen';
import {ConfirmationCodesEntry, EmailCheck, ForgotPassword, ForgotUsername, Password, Welcome} from '../modules/Auth/screens';

import {E_NAVIGATION_DESTINATIONS, RootStackParamList} from '../@types';
import {
  AccountSetupInfo,
  DlCapture,
  Email,
  EmailConfirmation,
  Name,
  Phone,
  PhoneConfirmation,
  PhotoCapture,
  RegisterProgress,
  Success,
  UserCredentials,
} from '../modules/Auth/screens';
import {makeId} from '../utils';
import {UnitList} from '../modules/Units/screens';
import injectFonts from '../utils/injectFonts';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../redux/reducers';
import {
  NavigationContainer,
  getPathFromState,
  getStateFromPath,
} from '@react-navigation/native';
import {setCurrentRoute} from './reducer';
import LoadingSpinner from '../components/LoadingSpinner';

import theme from '../theme';
import {
  Provider as PaperProvider,
  DarkTheme as PaperDarkTheme,
  DefaultTheme as PaperDefaultTheme,
} from 'react-native-paper';
import {NativeBaseProvider} from 'native-base';
import Ionicons from '@expo/vector-icons/Ionicons';
import InviteCode from '../modules/Auth/screens/InviteCode';
import utilities from '../tailwind.json';
import { TailwindProvider } from 'tailwind-rn';

injectFonts();

const Stack = createStackNavigator<RootStackParamList>();
const NavigatorLoadingScreen = () => <LoadingSpinner />;
const STATIC_ROUTE_SEGMENTS = new Set([
  'signin',
  'register',
  'info',
  'capturedl',
  'name',
  'capturephoto',
  'email',
  'confirmemail',
  'confirmphone',
  'credentials',
  'success',
  'forgotpassword',
  'forgotusername',
  'addwithunitcode',
  'generatelinkcode',
  'idcard',
  'unitcalendar',
  'dashboard',
  'pets',
  'storages',
  'parking',
  'violations',
  'directory',
  'settings',
  'keylogs',
  'visitorhistory',
  'accesshistory',
  'packageshistory',
  'doorlogs',
  'notificationdetails',
  'other',
  'vehicles',
  'workorders',
  'parkings',
  'gatecards',
  'visitorlogs',
  'alerts',
  'units',
  'people',
  'deliveries-packages',
  'others',
  'add-with-linkcode',
]);

function normalizeIncomingWebPath(path?: string) {
  if (!path) {
    return path;
  }
  const match = path.match(/^([^?#]*)([?#].*)?$/);
  const pathname = match?.[1] ?? path;
  const suffix = match?.[2] ?? '';
  const segments = pathname.split('/');

  const normalizedSegments = segments.map((segment, index) => {
    if (index === 0 || segment.length === 0) {
      return segment;
    }
    const lowerCased = segment.toLowerCase();
    return STATIC_ROUTE_SEGMENTS.has(lowerCased) ? lowerCased : segment;
  });

  return `${normalizedSegments.join('/')}${suffix}`;
}

export default function AppNavigator({
  navigationRef,
  initialState,
  onStateChange,
}) {
  const dispatch = useDispatch();

  const token = useSelector((state: RootState) => state?.auth?.token);
  const defaultUnitPeopleLink = useSelector(
    (state: RootState) =>
      state?.units?.unitLinks?.filter(link => link.isDefault === true)[0],
  );
  const hasUnitLinks = useSelector(
    (state: RootState) => (state?.units?.unitLinks?.length ?? 0) > 0,
  );
  const [routesWithPermissions, setRoutesWithPermission] = useState([]);
  
  useEffect(() => {
    let unmounted = false;
    const loadRoutes = async () => {
      if (!token) {
        setRoutesWithPermission([]);
        return;
      }
      try {
        const routesModule = await import('./routes');
        if (unmounted) {
          return;
        }
        setRoutesWithPermission(
          routesModule.default(defaultUnitPeopleLink, hasUnitLinks),
        );
      } catch (error) {
        if (!unmounted) {
          setRoutesWithPermission([]);
        }
      }
    };

    loadRoutes();
    return () => {
      unmounted = true;
    };
  }, [defaultUnitPeopleLink, hasUnitLinks, token]);
  const options = {
    cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
    headerShown: false,
  };

  const linking = {
    screens: {
      'Sign In': 'signin',
      'Account Setup Info': 'register/info',
      'Dl Capture': 'register/captureDL',
      Name: 'register/name',
      'Photo Capture': 'register/capturePhoto',
      Email: 'register/email',
      Phone: 'regsiter/phone',
      'Email Confirmation': 'register/confirmEmail',
      'Phone Confirmation': 'register/confirmPhone',
      'User Credentials': 'register/credentials',
      Success: 'register/success',
      'Forgot Password': 'forgotPassword',
      'Add with UnitCode': 'addwithUnitCode',
      'Generate Link Code': 'generateLinkCode',
      'ID Card': '/idCard',
      'Unit Calendar': '/unitCalendar',
      Dashboard: '/dashboard',
      Pets: '/pets',
      Storages: '/storages',
      'Parking List': '/parking',
      Violations: '/violations',
      Directory: '/directory',
      'Key Logs': '/keyLogs',
      'Visitor History': '/visitorHistory',
      'Access History': '/accessHistory',
      [E_NAVIGATION_DESTINATIONS.PACKAGES_HISTORIES]: '/packagesHistory',
      'Door Logs': '/doorLogs',
      'Notification Details': '/notificationDetails',

      Vehicles: '/other/vehicles',
      WorkOrders: '/other/workOrders',
      Storages: '/other/storages',
      Parkings: '/other/parkings',
      'Gate Cards': '/other/gateCards',
      Violations: '/other/violations',

      Directory: '/other/directory',
      'Edit Directory Settings': '/other/directory/settings',

      'Visitor Logs': '/other/visitorLogs',
      'Key Logs': '/other/keyLogs',
      'Door Logs': '/other/doorLogs',
      [E_NAVIGATION_DESTINATIONS.PACKAGES_HISTORIES]: '/other/packagesHistory',

      Alerts: '/alerts',
      Units: '/units',
      People: '/people',
      [E_NAVIGATION_DESTINATIONS.PACKAGES]: '/deliveries',
      'Deliveries & Packages': '/deliveries-packages',
      Others: '/others',

      'Add with UnitCode': '/units/add-with-linkcode',

      '*': '/'
    },
    getStateFromPath: (path, options) => {
      const normalizedPath = normalizeIncomingWebPath(path);
      let state = getStateFromPath(normalizedPath, options);
      return state;
    },
    getPathFromState: (state, config) => {
      let path = getPathFromState(state, config);
      return path;
    },
  };

  return (
    <NavigationContainer
      ref={navigationRef}
      initialState={initialState}
      onStateChange={state => {
        onStateChange(state);
        const currentRouteName = navigationRef?.current?.getCurrentRoute().name;
        dispatch(setCurrentRoute(currentRouteName));
      }}
      linking={linking}>
      <PaperProvider
        settings={{
          icon: props => <Ionicons {...props} />,
        }}
        theme={PaperDefaultTheme}>
        <NativeBaseProvider theme={theme}>
        <TailwindProvider utilities={utilities}>
          <Stack.Navigator>
            {token === null || token === undefined ? (
              <>
                <Stack.Screen
                  name="Sign In"
                  component={SignInScreen}
                  options={{...options, animationTypeForReplace: 'pop'}}
                  key={makeId(12)}
                />
                <Stack.Screen
                  name="Welcome"
                  component={Welcome}
                  options={{...options, animationTypeForReplace: 'pop'}}
                  key={makeId(12)}
                />
                <Stack.Screen
                  name="Account Setup Info"
                  component={AccountSetupInfo}
                  options={{...options, animationTypeForReplace: 'pop'}}
                  key={makeId(12)}
                />
                <Stack.Screen
                  name="Invite Code"
                  component={InviteCode}
                  options={{...options, animationTypeForReplace: 'pop'}}
                  key={makeId(12)}
                />
                <Stack.Screen
                  name="Dl Capture"
                  component={DlCapture}
                  options={{...options, animationTypeForReplace: 'pop'}}
                  key={makeId(12)}
                />
                <Stack.Screen
                  name="Name"
                  component={Name}
                  options={{...options, animationTypeForReplace: 'pop'}}
                  key={makeId(12)}
                />
                <Stack.Screen
                  name="Photo Capture"
                  component={PhotoCapture}
                  options={{...options, animationTypeForReplace: 'pop'}}
                  key={makeId(12)}
                />
                <Stack.Screen
                  name="Email"
                  component={Email}
                  options={{...options, animationTypeForReplace: 'pop'}}
                />
                <Stack.Screen
                  name="Phone"
                  component={Phone}
                  options={{...options, animationTypeForReplace: 'pop'}}
                  key={makeId(12)}
                />
                <Stack.Screen
                  name="Email Confirmation"
                  component={EmailConfirmation}
                  options={{...options, animationTypeForReplace: 'pop'}}
                  key={makeId(12)}
                />

                <Stack.Screen
                  name="Register Progress"
                  component={RegisterProgress}
                  options={{...options, animationTypeForReplace: 'pop'}}
                  key={makeId(12)}
                />
                <Stack.Screen
                  name="Phone Confirmation"
                  component={PhoneConfirmation}
                  options={{...options, animationTypeForReplace: 'pop'}}
                  key={makeId(12)}
                />
                <Stack.Screen
                  name={E_NAVIGATION_DESTINATIONS.CONFIRIRMATION_CODES_ENTRY}
                  component={ConfirmationCodesEntry}
                  options={{...options, animationTypeForReplace: 'pop'}}
                  key={makeId(12)}
                />
                <Stack.Screen
                  name="User Credentials"
                  component={UserCredentials}
                  options={{...options, animationTypeForReplace: 'pop'}}
                  key={makeId(12)}
                />
                <Stack.Screen
                  name="Success"
                  component={Success}
                  options={{...options, animationTypeForReplace: 'pop'}}
                  key={makeId(12)}
                />
                <Stack.Screen
                  name="Forgot Password"
                  component={ForgotPassword}
                  options={{...options, animationTypeForReplace: 'pop'}}
                  key={makeId(12)}
                />
                <Stack.Screen 
                 name="Email Check"
                 component={EmailCheck}
                 options={{...options, animationTypeForReplace: 'pop'}}
                 key={makeId(12)}
                />
                <Stack.Screen
                  name="Password"
                  component={Password}
                  options={{...options, animationTypeForReplace: 'pop'}}
                  key={makeId(12)}
                />
                <Stack.Screen
                  name={E_NAVIGATION_DESTINATIONS.FORGOT_USERNAME}
                  component={ForgotUsername}
                  options={{...options, animationTypeForReplace: 'pop'}}
                  key={makeId(12)}
                />
              </>
            ) : (
              <>
                {(defaultUnitPeopleLink === null ||
                  defaultUnitPeopleLink === undefined) && (
                    <Stack.Screen
                      name="Units"
                      component={UnitList}
                      options={{...options}}
                      key={makeId(12)}
                    />
                  )}
                {routesWithPermissions.length === 0 ? (
                  <Stack.Screen
                    name="Loading"
                    component={NavigatorLoadingScreen}
                    options={{...options}}
                    key="loading"
                  />
                ) : routesWithPermissions.map((route, index) => (
                        <Stack.Screen
                          name={route.name}
                          component={route.component}
                    options={{...route.options}}
                          key={index}
                        />
                ))}
              </>
            )}
          </Stack.Navigator>
          </TailwindProvider>
        </NativeBaseProvider>
      </PaperProvider>
    </NavigationContainer>
  );
}
